"use client";

import {
  DATA_LABEL_CLS,
  DISPLAY,
  EDITORIAL_SECTION_CLS,
  FaqSection,
  GROUND,
  Grain,
  LABEL_CLS,
  SECTION_TITLE_CLS,
  SURFACE_DARK,
} from "@/components/editorial";
import { FAQS, type FAQItem } from "@/lib/faqs";

import type { ConceptGroup } from "../spec";

/* ================================================================= */
/*  Shared copy                                                        */
/* ================================================================= */

const FAQ_INTRO =
  "Clear answers about data, model choice, deployment, cost and governance—grounded in the product available today.";

/** Look a FAQ up by the opening of its question so the objection cards and
 *  the spotlight stay in lockstep with `faqs.ts` rather than carrying a
 *  second copy of the same prose. Resolved once at module scope and loud on
 *  a miss: if a question in `faqs.ts` is reworded, the board fails to build
 *  instead of silently rendering an empty response. */
function faqItem(questionStart: string): FAQItem {
  const item = FAQS.find((entry) => entry.q.startsWith(questionStart));
  if (!item) {
    throw new Error(`FAQ not found for question starting: “${questionStart}”`);
  }
  return item;
}

/* ================================================================= */
/*  FAQ-001 — the live accordion                                       */
/* ================================================================= */

function LiveAccordion() {
  return (
    <FaqSection
      section="06"
      label="Buyer questions"
      numeral="06"
      title="What you control, and how it works."
      intro={FAQ_INTRO}
      items={FAQS}
    />
  );
}

/* ================================================================= */
/*  FAQ-002 — static two-column Q&A                                   */
/* ================================================================= */

export function TwoColumnQa() {
  return (
    <dl className="grid gap-x-12 gap-y-10 md:grid-cols-2 md:gap-x-14">
      {FAQS.map((item) => (
        <div
          key={item.q}
          className="border-t border-[color-mix(in_srgb,var(--theme-ink)_18%,transparent)] pt-5"
        >
          <dt
            className="text-[1.15rem] font-semibold leading-[1.3] tracking-[-0.01em] text-[var(--theme-ink)] md:text-[1.25rem]"
            style={DISPLAY}
          >
            {item.q}
          </dt>
          <dd className="m-0 mt-3 max-w-[52ch] text-[0.98rem] leading-[1.6] text-[color-mix(in_srgb,var(--theme-ink)_88%,transparent)]">
            {item.a}
          </dd>
        </div>
      ))}
    </dl>
  );
}

/* ================================================================= */
/*  FAQ-003 — objection → response cards                              */
/* ================================================================= */

/** Each objection is the opening of a question in `faqs.ts`; the response
 *  is that question's answer, resolved once so the two never drift. */
const OBJECTIONS = [
  "Why not just use ChatGPT or Copilot?",
  "Do we need to migrate our legacy systems first?",
  "We're regulated.",
].map((objection) => ({ objection, response: faqItem(objection).a }));

function ObjectionCards() {
  return (
    <ul
      aria-label="Common objections and the response to each"
      className="grid gap-px bg-[var(--rule)] p-px md:grid-cols-3 md:grid-rows-[auto_auto_1fr]"
    >
      {OBJECTIONS.map(({ objection, response }) => (
        <li
          key={objection}
          className="grid bg-[var(--theme-ground)] md:row-span-3 md:grid-rows-subgrid"
        >
          <div className="px-5 pt-5 md:px-6 md:pt-6">
            <div className="flex items-center gap-3 text-[var(--theme-accent-text)]">
              <span className="size-2 shrink-0 bg-[var(--theme-dark)]" aria-hidden="true" />
              <span className={LABEL_CLS}>Objection</span>
            </div>
            <p
              className="mt-4 text-[1.35rem] font-semibold leading-[1.2] tracking-[-0.02em] text-[var(--theme-ink)] md:text-[1.5rem]"
              style={DISPLAY}
            >
              “{objection}”
            </p>
          </div>
          <div className="mx-5 mt-6 border-t border-dotted border-[var(--rule)] pt-4 md:mx-6">
            <p className={`text-[color-mix(in_srgb,var(--theme-ink)_80%,transparent)] ${DATA_LABEL_CLS}`}>
              Response
            </p>
          </div>
          <p className="px-5 pt-2 pb-6 text-[0.95rem] leading-[1.6] text-[color-mix(in_srgb,var(--theme-ink)_88%,transparent)] md:px-6 md:pb-7">
            {response}
          </p>
        </li>
      ))}
    </ul>
  );
}

/* ================================================================= */
/*  FAQ-004 — comparison ledger                                        */
/* ================================================================= */

const LEDGER_COLUMNS = ["A generic copilot", "An approved Twin answer"] as const;

/** Values mirror the governed-answer specimen on the home (Source · Version ·
 *  Freshness · Unsupported) plus two rows drawn from the FAQ answers on model
 *  choice and governed-query access. */
const LEDGER_ROWS = [
  ["Source", "Whatever it could reach", "Approved sources"],
  ["Version", "None", "Recorded per approval"],
  ["Freshness", "Unknown", "Current-to date stated"],
  ["Unsupported questions", "Answers anyway", "Says so"],
  ["Model choice", "One vendor", "Configured per task"],
  ["Write access", "Possible", "Not in a governed query"],
] as const;

/** Below `md` the table stacks: each row becomes a block with the property
 *  across the top and the two values side by side beneath it, so the Twin
 *  column is never pushed off-canvas behind a horizontal scroll. */
const LEDGER_VALUE_CLS =
  "px-4 py-2.5 text-[length:var(--type-data)] tracking-[0.04em] uppercase max-md:px-3 max-md:py-2";

function ComparisonLedger() {
  return (
    <div className="border border-[var(--rule)]">
      <div className="flex min-h-[2.65rem] items-center justify-between gap-4 border-b border-dotted border-[var(--rule)] px-4 py-[0.7rem] max-md:flex-col max-md:items-start max-md:gap-1">
        <span className={`text-[color-mix(in_srgb,var(--theme-ink)_80%,transparent)] ${DATA_LABEL_CLS}`}>
          Exhibit — what each answer carries
        </span>
        <strong className={`text-[var(--theme-accent-text)] ${DATA_LABEL_CLS}`}>
          Generic copilot · approved Twin answer
        </strong>
      </div>
      <table className="w-full border-collapse max-md:block">
        <caption className="sr-only">
          How a generic copilot and an approved Twin answer differ on source,
          version, freshness, unsupported questions, model choice and write
          access.
        </caption>
        <thead className="max-md:block">
          <tr className="border-b border-dotted border-[var(--rule)] max-md:grid max-md:grid-cols-2">
            <th scope="col" className="sr-only">
              Property
            </th>
            {LEDGER_COLUMNS.map((column, index) => (
              <th
                key={column}
                scope="col"
                className={`border-l border-dotted border-[var(--rule)] px-4 py-3 text-left max-md:px-3 ${DATA_LABEL_CLS} ${
                  index === 0
                    ? "text-[color-mix(in_srgb,var(--theme-ink)_80%,transparent)] max-md:border-l-0"
                    : "text-[var(--theme-accent-text)]"
                }`}
              >
                {column}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="max-md:block">
          {LEDGER_ROWS.map(([label, copilot, twin]) => (
            <tr
              key={label}
              className="border-b border-dotted border-[var(--rule)] last:border-b-0 max-md:grid max-md:grid-cols-2"
            >
              <th
                scope="row"
                className="px-3 py-2.5 text-left text-[length:var(--type-label-small)] font-medium leading-tight tracking-[0.16em] text-[color-mix(in_srgb,var(--theme-ink)_80%,transparent)] uppercase max-md:col-span-2 max-md:border-b max-md:border-dotted max-md:border-[var(--rule)] max-md:pt-2.5 max-md:pb-2 max-md:tracking-[0.12em] md:w-[30%] md:min-w-[9.5rem]"
              >
                {label}
              </th>
              <td
                className={`border-l border-dotted border-[var(--rule)] font-medium text-[color-mix(in_srgb,var(--theme-ink)_80%,transparent)] max-md:border-l-0 ${LEDGER_VALUE_CLS}`}
              >
                {copilot}
              </td>
              <td
                className={`border-l border-dotted border-[var(--rule)] font-semibold text-[var(--theme-ink)] ${LEDGER_VALUE_CLS}`}
              >
                {twin}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="flex min-h-[2.2rem] items-baseline gap-2 border-t border-dotted border-[var(--rule)] px-4 py-[0.55rem]">
        <span className={`shrink-0 text-[color-mix(in_srgb,var(--theme-ink)_80%,transparent)] ${DATA_LABEL_CLS}`}>
          Basis
        </span>
        <span className="text-[length:var(--type-data)] font-medium tracking-[0.04em] text-[color-mix(in_srgb,var(--theme-ink)_88%,transparent)] uppercase">
          Governed query behaviour in the product available today
        </span>
      </div>
    </div>
  );
}

/* ================================================================= */
/*  FAQ-005 — single-question spotlight (dark band)                    */
/* ================================================================= */

const SPOTLIGHT = faqItem("What happens when the Twin cannot support a question?");
const SPOTLIGHT_LEAD = "It says so.";

/** The answer with its opening sentence removed, since that sentence is set
 *  as the oversized lead. Falls back to the whole answer if `faqs.ts` ever
 *  stops opening with it. */
const SPOTLIGHT_BODY = SPOTLIGHT.a.startsWith(SPOTLIGHT_LEAD)
  ? SPOTLIGHT.a.slice(SPOTLIGHT_LEAD.length).trim()
  : SPOTLIGHT.a;

const SPOTLIGHT_LEDGER = [
  ["Identifies", "Approved product · version · freshness"],
  ["If unsupported", "Returns ‘not supported’"],
  ["Never", "A plausible guess"],
] as const;

function QuestionSpotlight() {
  return (
    <section
      aria-labelledby="faq-spotlight-heading"
      className="relative overflow-hidden"
      style={{ backgroundColor: SURFACE_DARK, color: GROUND }}
    >
      <Grain opacity={0.45} />
      <div className={`relative z-10 ${EDITORIAL_SECTION_CLS}`}>
        <div className="flex items-center gap-3 text-[var(--theme-accent)]">
          <span className="size-2 shrink-0 bg-[var(--theme-accent)]" aria-hidden="true" />
          <p className={LABEL_CLS}>Buyer question — unsupported</p>
        </div>

        <div className="mt-10 grid grid-cols-2 gap-x-14 gap-y-10 max-lg:grid-cols-1">
          <h2
            id="faq-spotlight-heading"
            className={`max-w-[16ch] text-[var(--theme-ground)] ${SECTION_TITLE_CLS}`}
            style={DISPLAY}
          >
            {SPOTLIGHT.q}
          </h2>
          <div className="lg:pt-2">
            <p
              className="text-[clamp(3rem,6vw,5.5rem)] font-semibold leading-[0.98] tracking-[-0.04em] text-[var(--theme-accent)]"
              style={DISPLAY}
            >
              {SPOTLIGHT_LEAD}
            </p>
            <p
              className="mt-8 max-w-[44ch] text-[1.15rem] font-medium leading-[1.5] tracking-[-0.01em] text-[color-mix(in_srgb,var(--theme-ground)_88%,transparent)] md:text-[1.35rem]"
              style={DISPLAY}
            >
              {SPOTLIGHT_BODY}
            </p>
          </div>
        </div>

        <dl className="mt-16 grid grid-cols-3 gap-px border-y border-[var(--rule)] bg-[var(--rule)] max-md:grid-cols-1 md:mt-20">
          {SPOTLIGHT_LEDGER.map(([label, value]) => (
            <div
              key={label}
              className="bg-[var(--theme-surface-dark)] py-5 pr-6 md:px-6 md:first:pl-0"
            >
              <dt className={`text-[color-mix(in_srgb,var(--theme-ground)_80%,transparent)] ${DATA_LABEL_CLS}`}>
                {label}
              </dt>
              <dd className="m-0 mt-2 text-[length:var(--type-data)] font-medium tracking-[0.04em] text-[color-mix(in_srgb,var(--theme-ground)_92%,transparent)] uppercase">
                {value}
              </dd>
            </div>
          ))}
        </dl>
      </div>
    </section>
  );
}

/* ================================================================= */
/*  Group                                                              */
/* ================================================================= */

export const FAQ_GROUP: ConceptGroup = {
  id: "faq",
  prefix: "FAQ",
  title: "FAQ & objections",
  blurb:
    "Ways of answering the buyer's questions: the live accordion, a flat Q&A spread, objections paired with their responses, a side-by-side ledger and a single question given the whole band.",
  specs: [
    {
      id: "FAQ-001",
      title: "Accordion",
      note: "The live section 06 as shipped: marker, title, intro and the single-open hairline accordion from FaqSection.",
      surface: "ground",
      width: "full",
      render: () => <LiveAccordion />,
    },
    {
      id: "FAQ-002",
      title: "Two-column Q&A",
      note: "Every FAQ laid out statically in two columns; hairline-topped items borrow the numbered-list anatomy without the numerals.",
      surface: "ground",
      width: "contained",
      render: () => <TwoColumnQa />,
    },
    {
      id: "FAQ-003",
      title: "Objection → response cards",
      note: "Three gap-px cards that state the buyer's objection as a quote and answer it with the FAQ response; idiom from the proof-band exhibits.",
      surface: "ground",
      width: "contained",
      render: () => <ObjectionCards />,
    },
    {
      id: "FAQ-004",
      title: "Comparison ledger",
      note: "Hairline table setting a generic copilot against an approved Twin answer, row for row; values from the governed-answer specimen on the home. Stacks per row below md.",
      surface: "ground",
      width: "contained",
      render: () => <ComparisonLedger />,
    },
    {
      id: "FAQ-005",
      title: "Single-question spotlight",
      note: "One FAQ given a whole dark band: the question as the H2 and the answer opening with an oversized “It says so.”",
      surface: "dark",
      width: "full",
      render: () => <QuestionSpotlight />,
    },
  ],
};
